/**
 * RegisterPage - User registration with invite token support
 * Feature: 647-invite-registration
 * Feature: 892-invite-redirect - Post-authentication redirect support
 *
 * Routes:
 * - /register?invite=<nonce>&returnUrl=<path> - Registration with invite and destination
 * - /register?invite=<nonce> - Registration with invite token
 * - /register - Invitation only message (no public registration)
 */

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Container } from '@/components/layout';
import { RegisterForm } from '@/components/composed';
import { Heading, Text } from '@/components/atoms';
import { useValidateInviteTokenGraphQL } from '@/hooks/graphql/useInviteGraphQL';
import logger from '@/utils/logger';

type PageState = 'loading' | 'valid' | 'invalid' | 'no-invite';

export function RegisterPage() {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  // Feature: 892-invite-redirect - Read returnUrl for post-registration redirect
  const returnUrl = searchParams.get('returnUrl');

  // Feature: 892-invite-redirect - Construct login URL with returnUrl preserved
  const loginUrl = returnUrl
    ? `/login?returnUrl=${encodeURIComponent(returnUrl)}`
    : '/login';

  const [pageState, setPageState] = useState<PageState>(inviteToken ? 'loading' : 'no-invite');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [usesRemaining, setUsesRemaining] = useState<number | null>(null);

  const { validateInviteToken, loading } = useValidateInviteTokenGraphQL();

  useEffect(() => {
    if (!inviteToken) {
      setPageState('no-invite');
      return;
    }

    // Validate the invite token
    const validate = async () => {
      try {
        const result = await validateInviteToken(inviteToken);
        if (result?.isValid) {
          setPageState('valid');
          setUsesRemaining(result.usesRemaining);
        } else {
          setPageState('invalid');
          setErrorMessage(result?.message || 'Invalid invite token');
        }
      } catch (err) {
        logger.error('Auth', 'Error validating invite token', err);
        setPageState('invalid');
        setErrorMessage('Failed to validate invite token');
      }
    };

    validate();
  }, [inviteToken, validateInviteToken]);

  // Loading state
  if (pageState === 'loading' || loading) {
    return (
      <Container maxWidth="sm">
        <div className="text-center py-12">
          <p className="text-muted">Validating invite...</p>
        </div>
      </Container>
    );
  }

  // No invite token - show invitation only message
  if (pageState === 'no-invite') {
    return (
      <Container maxWidth="sm">
        <div className="text-center py-12">
          <Heading level="h1" className="mb-4">Registration is Invitation Only</Heading>
          <Text className="text-muted mb-6">
            206mp is currently invite-only. If you have an invite link, please use it to register.
          </Text>
          <Text className="text-muted">
            Already have an account?{' '}
            <Link to={loginUrl} className="text-primary hover:underline">
              Log in
            </Link>
          </Text>
        </div>
      </Container>
    );
  }

  // Invalid or exhausted token
  if (pageState === 'invalid') {
    return (
      <Container maxWidth="sm">
        <div className="text-center py-12">
          <Heading level="h1" className="mb-4 text-danger">Invalid Invite Link</Heading>
          <Text className="text-muted mb-6">
            {errorMessage || 'This invite link is invalid or has expired.'}
          </Text>
          <Text className="text-muted">
            Already have an account?{' '}
            <Link to={loginUrl} className="text-primary hover:underline">
              Log in
            </Link>
          </Text>
        </div>
      </Container>
    );
  }

  // Valid token - show registration form
  return (
    <Container maxWidth="sm">
      <Heading level="h1">Register</Heading>
      {usesRemaining !== null && usesRemaining < 10 && (
        <Text className="text-amber-500 text-sm mt-2">
          This invite has {usesRemaining} {usesRemaining === 1 ? 'use' : 'uses'} remaining.
        </Text>
      )}
      <RegisterForm inviteToken={inviteToken!} returnUrl={returnUrl || undefined} />
    </Container>
  );
}
